import { runtimeConfig } from './config/runtime-config';
import { defaultAppLocale, isAppLocale } from './i18n/locales';
import { getClientCookieValue } from './utils/auth-cookies';
import { UTM_COOKIE, getUtmHeaders } from './utils/utm';

export type RequestConfig<TData = unknown> = {
  url?: string;
  method: 'GET' | 'PUT' | 'PATCH' | 'POST' | 'DELETE';
  params?: { [val: string]: any };
  data?: TData | FormData;
  responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'text' | 'stream';
  signal?: AbortSignal;
  cache?: RequestCache;
  headers?: HeadersInit;
  publicRequest?: boolean;
  handle401?: boolean; // default true
  next?: NextFetchRequestConfig;
};

export type ResponseConfig<TData = unknown> = {
  data: TData;
  status: number;
  statusText: string;
};

const protectedRoutes = [
  '/product/create',
  '/product/edit',
  'my/avatar',
  'my/data',
  'my/settings',
  'my/support',
  'my/favorites',
];

const BASE_URL_SERVER = process.env.API_URL_INTERNAL || process.env.API_URL || 'https://indudu-api.waive.dev';
const BASE_URL_CLIENT = process.env.NEXT_PUBLIC_API_URL || process.env.API_URL || 'https://indudu-api.waive.dev';

// ---- helpers (cookies) ----
const TOKEN_COOKIE = 'token';
const REFRESH_COOKIE = 'refreshToken';
const LANGUAGE_COOKIE = 'language';
const CURRENCY_COOKIE = 'doska-currency';
const defaultCurrency = runtimeConfig.isMultiCountry ? 'RUB' : runtimeConfig.defaultCurrency;
const WL_DOMAIN = process.env.WL_DOMAIN;

async function getServerCookies() {
  const { cookies } = await import('next/headers');
  return cookies();
}

async function getServerHeaders() {
  const { headers } = await import('next/headers');
  return headers();
}

function getClientCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1];
}

async function getCookie(name: string): Promise<string | undefined> {
  if (typeof window === 'undefined') {
    const all = await getServerCookies();
    return all.get(name)?.value;
  } else {
    return getClientCookie(name);
  }
}

async function setCookie(name: string, value: string, maxAgeSeconds = 60 * 60 * 24 * 30) {
  if (typeof window === 'undefined') {
    // Server side
    const { cookies } = await import('next/headers');
    (await cookies()).set(name, value, { httpOnly: false, path: '/', maxAge: maxAgeSeconds });
  } else {
    // Client side
    document.cookie = `${name}=${value}; path=/; max-age=${maxAgeSeconds}`;
  }
}

// Small utility so we don't attach content-type for FormData
function mergeHeaders(base: HeadersInit, extra?: HeadersInit): HeadersInit {
  return { ...(base as any), ...(extra as any) };
}

// ---- refresh logic ----
type RefreshResponse = { accessToken: string; refreshToken?: string };

// Prevent concurrent refresh bursts on the client
let refreshPromise: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  const BASE_URL = typeof window === 'undefined' ? BASE_URL_SERVER : BASE_URL_CLIENT;

  // If we're in the browser and a refresh is ongoing, reuse it
  if (typeof window !== 'undefined' && refreshPromise) return refreshPromise;

  const doRefresh = async () => {
    try {
      const refreshToken = await getCookie(REFRESH_COOKIE);
      if (!refreshToken) return false;

      const utmCookie =
        typeof window === 'undefined'
          ? (await getServerCookies()).get(UTM_COOKIE)?.value
          : getClientCookieValue(document.cookie, UTM_COOKIE);
      const res = await fetch(`${BASE_URL}/api/telegram/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getUtmHeaders(utmCookie) },
        body: JSON.stringify({ refreshToken }),
      });

      if (!res.ok) return false;

      const body: RefreshResponse = await res.json().catch(() => null as any);
      if (!body?.accessToken) return false;

      await setCookie(TOKEN_COOKIE, body.accessToken, 86400);
      if (body.refreshToken) {
        await setCookie(REFRESH_COOKIE, body.refreshToken);
      }
      return true;
    } catch {
      return false;
    }
  };

  if (typeof window === 'undefined') {
    // On the server, just run it (no global shared state per request anyway)
    return doRefresh();
  } else {
    // In the browser, memoize while it runs
    refreshPromise = doRefresh();
    const ok = await refreshPromise.finally(() => {
      refreshPromise = null;
    });
    return ok;
  }
}

function toSearchParams(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined) return;

    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, String(v)));
    } else if (typeof value === 'object' && Object.keys(value).length === 0) {
      return;
    } else {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
}

// ---- main client ----
export type ResponseErrorConfig<T = any> = T;

const client = async <TData, TError = unknown, TVariables = unknown>(
  config: RequestConfig<TVariables>,
): Promise<ResponseConfig<TData>> => {
  const BASE_URL = typeof window === 'undefined' ? BASE_URL_SERVER : BASE_URL_CLIENT;
  const handle401 = config.handle401 !== false;

  // Build URL
  let url = `${BASE_URL}${config.url || ''}`;
  if (config.params) {
    url += `?${toSearchParams(Object.fromEntries(Object.entries(config.params).filter(([, v]) => v !== undefined)) as any)}`;
  }

  const isFormData = config.data instanceof FormData;

  // Base headers
  let headers: HeadersInit = {
    ...(config.headers || {}),
  };
  if (!isFormData) headers = mergeHeaders(headers, { 'Content-Type': 'application/json' });
  headers = mergeHeaders(headers, { 'x-wl-domain': WL_DOMAIN || '' });
  // Attach auth + language from cookies
  if (!config.publicRequest && typeof window === 'undefined') {
    const allCookies = await getServerCookies();
    const allHeaders = await getServerHeaders();
    const token = allCookies.get(TOKEN_COOKIE)?.value;
    const language = allCookies.get(LANGUAGE_COOKIE)?.value;
    const currency = allCookies.get(CURRENCY_COOKIE)?.value || defaultCurrency;
    const userAgent = allHeaders.get('user-agent');
    const internalSecret = process.env.INTERNAL_SHARED_SECRET;
    if (token) headers = mergeHeaders(headers, { Authorization: `Bearer ${token}` });
    if (language) headers = mergeHeaders(headers, { 'x-language': language });
    if (currency) headers = mergeHeaders(headers, { 'x-currency': currency });
    headers = mergeHeaders(headers, getUtmHeaders(allCookies.get(UTM_COOKIE)?.value));
    if (userAgent) headers = mergeHeaders(headers, { 'User-Agent': userAgent });
    if (internalSecret) {
      headers = mergeHeaders(headers, { 'X-Internal-Token': internalSecret });
    }
    headers = mergeHeaders(headers, { 'next-server': 'true' });
  } else if (!config.publicRequest) {
    const token = getClientCookie(TOKEN_COOKIE);
    const language = getClientCookie(LANGUAGE_COOKIE);
    const currency = getClientCookie(CURRENCY_COOKIE) || defaultCurrency;
    if (token) headers = mergeHeaders(headers, { Authorization: `Bearer ${token}` });
    if (language) headers = mergeHeaders(headers, { 'x-language': language });
    if (currency) headers = mergeHeaders(headers, { 'x-currency': currency });
    headers = mergeHeaders(headers, getUtmHeaders(getClientCookieValue(document.cookie, UTM_COOKIE)));
  }

  // A small helper to actually perform the fetch
  const doFetch = async (): Promise<Response> => {
    return fetch(url, {
      method: config.method.toUpperCase(),
      body: isFormData
        ? (config.data as FormData)
        : config.data !== undefined
          ? JSON.stringify(config.data)
          : undefined,
      signal: config.signal,
      cache: config.cache,
      headers,
      next: config.next,
    });
  };

  let response = await doFetch();

  // Handle 401 with refresh-then-retry (avoid looping on the refresh endpoint itself)
  const isRefreshEndpoint = url.startsWith(`${BASE_URL}/api/telegram/auth/refresh`);
  if (response.status === 401 && !isRefreshEndpoint) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      // Rebuild headers with the new token
      headers = { ...(headers as any) };
      const newToken = await getCookie(TOKEN_COOKIE);
      if (newToken) headers['Authorization'] = `Bearer ${newToken}`;
      response = await doFetch();
    } else {
      const isAtProtected = protectedRoutes.some((val) => window.location.href.includes(val));

      // Couldn’t refresh -> go to login (client only)
      if (typeof window !== 'undefined' && (handle401 || isAtProtected)) {
        const language = getClientCookie(LANGUAGE_COOKIE);
        const targetLocale = isAppLocale(language) ? language : defaultAppLocale;

        window.location.href = targetLocale === defaultAppLocale ? '/?login=true' : `/${targetLocale}/?login=true`;
      }
    }
  }

  // Non-OK after retry?
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw {
      status: response.status,
      statusText: response.statusText,
      data: body,
    } as {
      status: number;
      statusText: string;
      data: TError;
    };
  }

  // Empty body handling
  const len = response.headers.get('content-length');
  if (
    len === '0' &&
    (response.status === 200 || response.status === 201 || response.status === 204 || response.status === 205)
  ) {
    return {
      data: null as any,
      status: response.status,
      statusText: response.statusText,
    };
  }

  // Parse JSON by default (extend as needed by responseType)
  const data = config.responseType === 'text' ? await response.text() : await response.json().catch(() => null);

  return {
    data,
    status: response.status,
    statusText: response.statusText,
  };
};

export default client;
